import {
  INVOICE_FILTER_API_URL,
  INVOICE_UPLOAD_API_URL,
  MILESTONES_API_URL,
  PROJECT_INVOICE_STATUS_UPDATE_API_URL,
  MILESTONE_STATUS_UPDATE_API_URL,
} from "../config/constants";
import { requestNotificationsRefresh } from "./notificationsApi";
import { apiFetch } from "../utils/apiClient";






const getAuthHeader = () => {
  const rawToken =
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("accessToken");
  const tokenType = sessionStorage.getItem("tokenType") || "Bearer";

  if (!rawToken) {
    throw new Error("Authentication token not found. Please log in again.");
  }

  const normalizedToken = rawToken
    .replace(/^"|"$/g, "")
    .replace(/^Bearer\s+/i, "")
    .trim();

  return `${tokenType} ${normalizedToken}`;
};

export const fetchMilestoneTrackerData = async (
  pageNumber = 0,
  pageSize = 10,
  params,
) => {
  const requestUrl = new URL(MILESTONES_API_URL, globalThis.location.origin);
  requestUrl.searchParams.set("page", String(pageNumber));
  requestUrl.searchParams.set("size", String(pageSize));

  if (params?.approvalStatusInFG) {
    requestUrl.searchParams.set("approvalStatusInFG", params.approvalStatusInFG);
  }
  if (params?.revenueMonth) {
    requestUrl.searchParams.set("revenueMonth", params.revenueMonth);
  }
  if (params?.milestoneStatus) {
    requestUrl.searchParams.set("milestoneStatus", params.milestoneStatus);
  }
  if (params?.searchKeyword) {
    requestUrl.searchParams.set("searchKeyword", params.searchKeyword);
  }
  if (params?.startDate) {
    requestUrl.searchParams.set("startDate", params.startDate);
  }
  if (params?.endDate) {
    requestUrl.searchParams.set("endDate", params.endDate);
  }

  const res = await apiFetch(requestUrl.toString(), {
    method: "GET",
    headers: {
      Authorization: getAuthHeader(),
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  }, "Failed to fetch milestones. Please try again.", { passthrough: [401, 403] });

  if (!res.ok) {
    if (res.status === 401) throw new Error("Unauthorized. Please log in again.");
    if (res.status === 403) throw new Error("You don't have permission to view milestones.");
    throw new Error(`Failed to fetch milestones (${res.status})`);
  }

  return (await res.json()) ;
};

export const updateInvoiceStatus = async (
  payload,
) => {
  const res = await apiFetch(PROJECT_INVOICE_STATUS_UPDATE_API_URL, {
    method: "PATCH",
    headers: {
      Authorization: getAuthHeader(),
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  }, "Failed to update invoice status. Please try again.");

  if (!res.ok) {
    let errMsg = `Failed to update invoice status (${res.status})`;
    try {
      const body = (await res.json()) ;
      if (body?.message) errMsg = body.message;
    } catch {
      // Keep fallback message if response body is not JSON.
    }
    throw new Error(errMsg);
  }

  // INVOICE_SUBMITTED / INVOICE_APPROVED / INVOICE_REJECTED
  requestNotificationsRefresh();
};

export const updateMilestoneStatus = async (
  payload
) => {
  const res = await apiFetch(MILESTONE_STATUS_UPDATE_API_URL, {
    method: "PATCH",
    headers: {
      Authorization: getAuthHeader(),
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  }, "Failed to update milestone status. Please try again.");

  if (!res.ok) {
    let errMsg = `Failed to update milestone status (${res.status})`;
    try {
      const body = (await res.json()) ;
      if (body?.message) errMsg = body.message;
    } catch {
      // keep fallback
    }
    throw new Error(errMsg);
  }

  // MILESTONE_COMPLETED
  requestNotificationsRefresh();
};

export const uploadInvoiceFile = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const res = await apiFetch(INVOICE_UPLOAD_API_URL, {
    method: "POST",
    headers: {
      Authorization: getAuthHeader(),
    },
    body: formData,
  }, "Failed to upload invoice. Please try again.");

  if (!res.ok) {
    let errMsg = `Failed to upload invoice (${res.status})`;
    try {
      const body = (await res.json());
      if (body?.message) errMsg = body.message;
    } catch {
      // Keep fallback message if response body is not JSON.
    }
    throw new Error(errMsg);
  }

  try {
    return (await res.json()) ;
  } catch {
    return { message: "Invoice upload completed" };
  } finally {
    // INVOICE_SUBMITTED
    requestNotificationsRefresh();
  }
};

export const filterInvoices = async (
  approvalStatusInFG,
  revenueMonth,
  startDate,
  endDate,
  searchKeyword,
  pageNumber = 0,
  pageSize = 10,
) => {
  const requestUrl = new URL(INVOICE_FILTER_API_URL, globalThis.location.origin);
  requestUrl.searchParams.set("page", String(pageNumber));
  requestUrl.searchParams.set("size", String(pageSize));

  if (approvalStatusInFG) {
    requestUrl.searchParams.set("approvalStatusInFG", approvalStatusInFG);
  }
  if (revenueMonth) {
    requestUrl.searchParams.set("revenueMonth", revenueMonth);
  }
  if (startDate) {
    requestUrl.searchParams.set("startDate", startDate);
  }
  if (endDate) {
    requestUrl.searchParams.set("endDate", endDate);
  }
  if (searchKeyword) {
    requestUrl.searchParams.set("searchKeyword", searchKeyword);
  }

  const res = await apiFetch(requestUrl.toString(), {
    method: "GET",
    headers: {
      Authorization: getAuthHeader(),
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  }, "Failed to filter invoices. Please try again.");

  if (!res.ok) {
    let errMsg = `Failed to filter invoices (${res.status})`;
    try {
      const body = (await res.json()) ;
      if (body?.message) errMsg = body.message;
    } catch {
      // Keep fallback message if response body is not JSON.
    }
    throw new Error(errMsg);
  }

  const payload = await res.json();
  if (Array.isArray(payload)) {
    return { invoices: payload };
  }

  const wrapped = payload ;
  const pagination =
    wrapped.invoicePagination ||
    wrapped.pagination ||
    wrapped.data?.invoicePagination;

  if (Array.isArray(wrapped.invoices)) {
    return { invoices: wrapped.invoices, invoicePagination: pagination };
  }
  if (Array.isArray(wrapped.content)) {
    return { invoices: wrapped.content, invoicePagination: pagination };
  }
  if (Array.isArray(wrapped.data?.invoices)) {
    return { invoices: wrapped.data.invoices, invoicePagination: pagination };
  }
  if (Array.isArray(wrapped.data?.content)) {
    return { invoices: wrapped.data.content, invoicePagination: pagination };
  }

  return { invoices: [], invoicePagination: pagination };
};
